import React, { useEffect, useState } from "react";
import { ArrowUpRight, Linkedin } from "lucide-react";
import { openBookConsultation } from "../lib/bookConsultation";
import { fetchSiteSettings } from "../lib/siteSettings";
import Logo from "./Logo";

const DEFAULTS = {
  tagline_small: "— Let's talk pipeline",
  tagline_heading: "Have a brief? Send it over.",
  cta_text: "Book a consultation",
  reach_email: "hi@dotconverse.com",
  reach_phone: "+91 124 000 0000",
  reach_city: "Delhi NCR · India",
  reach_office: "Faridabad, Haryana",
  reach_hours: "Mon–Fri · 10–7 IST",
  linkedin_url: "https://www.linkedin.com/company/dotconverse",
  copyright_name: "dotconverse",
  services_links: [
    { label: "B2B Demand Generation", href: "/services/b2b-demand-generation" },
    { label: "B2B Nurture Marketing", href: "/services/b2b-nurture-marketing" },
    { label: "B2B Content Marketing", href: "/services/b2b-content-marketing" },
    { label: "ABM & CX", href: "/services/abm-cx-kaboom" },
    { label: "AI Search Optimization", href: "/services/ai-powered-marketing" },
  ],
  company_links: [
    { label: "About", href: "/about" },
    { label: "Our Thinking", href: "/thinking" },
    { label: "Careers", href: "/about" },
    { label: "Press", href: "/about" },
    { label: "Contact", href: "/contact" },
  ],
  resources_links: [
    { label: "Case Studies", href: "/work" },
    { label: "Insights", href: "/thinking" },
    { label: "Playbooks", href: "/thinking" },
    { label: "Guides", href: "/thinking" },
    { label: "Strategy Questionnaire", href: "/strategy" },
    { label: "Credentials Deck", href: "/credentials" },
    { label: "Newsletter", href: "/contact" },
  ],
};

function splitHeading(h) {
  // Split at first "?" so the remainder renders in italic serif.
  if (!h) return [h, ""];
  const qIdx = h.indexOf("?");
  if (qIdx > -1) return [h.slice(0, qIdx + 1), h.slice(qIdx + 1).trim()];
  const parts = h.split(/[.!]/);
  if (parts.length > 1) return [parts[0] + (h.includes("?") ? "" : "."), parts.slice(1).join(".").trim()];
  return [h, ""];
}

export default function Footer() {
  const [s, setS] = useState(DEFAULTS);

  useEffect(() => {
    fetchSiteSettings().then((data) => {
      if (data) setS({ ...DEFAULTS, ...data });
    });
  }, []);

  const [head1, head2] = splitHeading(s.tagline_heading);

  // Ensure the Strategy Questionnaire + Credentials Deck always appear in
  // Resources even if the CMS settings overrode `resources_links` before these
  // routes existed.
  const resourcesLinks = (() => {
    const links = Array.isArray(s.resources_links) ? s.resources_links : [];
    let out = [...links];
    if (!out.some((l) => l?.href === "/strategy")) {
      out = [...out, { label: "Strategy Questionnaire", href: "/strategy" }];
    }
    if (!out.some((l) => l?.href === "/credentials")) {
      out = [...out, { label: "Credentials Deck", href: "/credentials" }];
    }
    return out;
  })();

  // Filter LinkedIn Presence out of Services in case a saved CMS `services_links`
  // still carries the old entry.
  const servicesLinks = (() => {
    const links = Array.isArray(s.services_links) ? s.services_links : [];
    const out = links.filter((l) => l?.href !== "/services/linkedin-presence");
    if (!out.some((l) => l?.href === "/ai-seo")) out.push({ label: "Get Free AI-SEO Audit worth Rs 25000/-", href: "/ai-seo" });
    return out;
  })();

  const groups = [
    { title: "Services", links: servicesLinks },
    { title: "Company", links: s.company_links },
    { title: "Resources", links: resourcesLinks },
    {
      title: "Reach us",
      links: [
        { label: s.reach_email, href: `mailto:${s.reach_email}` },
        { label: s.reach_phone, href: `tel:${(s.reach_phone || "").replace(/\s+/g, "")}` },
        { label: s.reach_city, href: "/about" },
        { label: s.reach_office, href: "/about" },
        { label: s.reach_hours, href: "/contact" },
      ].filter((l) => l.label),
    },
  ];

  return (
    <footer
      data-testid="site-footer"
      className="section-dark border-t border-white/5"
    >
      <div className="container-xl py-20 md:py-28">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-12 pb-20">
          <div className="max-w-2xl">
            <div className="font-serif italic text-[#FFB000] text-sm tracking-wide mb-4">
              {s.tagline_small}
            </div>
            <h2 className="font-display text-5xl md:text-7xl tracking-tighter leading-[0.95] text-white">
              {head1}
              {head2 && (
                <>
                  {" "}
                  <span className="font-serif italic font-light text-white/70">
                    {head2}
                  </span>
                </>
              )}
            </h2>
          </div>
          <button
            type="button"
            onClick={() => openBookConsultation("footer")}
            data-testid="footer-cta-contact"
            className="btn-pill btn-primary-amber text-sm self-start"
          >
            {s.cta_text}
            <ArrowUpRight size={14} strokeWidth={2.2} />
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-8 pb-20">
          {groups.map((g) => (
            <div key={g.title}>
              <div className="text-[11px] uppercase tracking-[0.22em] text-white/40 mb-5">
                {g.title}
              </div>
              <ul className="space-y-3">
                {g.links.map((l) => {
                  // Highlight LinkedIn Presence offer link with a small inline
                  // icon + LinkedIn-blue hover accent — visually flags it as
                  // a distinct, high-intent offer line within the agency
                  // services column.
                  const isLinkedIn = l.href === "/services/linkedin-presence";
                  return (
                    <li key={`${g.title}-${l.label}`}>
                      <a
                        href={l.href || "#"}
                        data-testid={`footer-link-${String(l.label).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className={`inline-flex items-center gap-2 transition-colors text-[15px] ${
                          isLinkedIn
                            ? "text-white/85 hover:text-[#0A66C2]"
                            : "text-white/75 hover:text-white"
                        }`}
                      >
                        {isLinkedIn && (
                          <Linkedin
                            size={14}
                            strokeWidth={2}
                            className="shrink-0"
                            style={{ color: "#0A66C2" }}
                          />
                        )}
                        {l.label}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 pt-10 border-t border-white/10">
          <div className="flex items-center gap-4">
            <Logo dark height={20} testId="footer-logo" />
            <span className="h-4 w-px bg-white/15 hidden sm:block" />
            <div className="flex items-center gap-3 text-white/50 text-sm">
              <span className="h-2 w-2 rounded-full bg-[#FFB000]" />
              <span className="font-display">
                © {new Date().getFullYear()} {s.copyright_name}. All rights reserved.
              </span>
            </div>
          </div>
          <div className="flex items-center gap-6 text-sm text-white/50">
            <a href="/privacy" data-testid="footer-privacy" className="hover:text-white">Privacy</a>
            <a href="/privacy" data-testid="footer-terms" className="hover:text-white">Terms</a>
            <a href="/privacy" data-testid="footer-cookies" className="hover:text-white">Cookies</a>
            {s.linkedin_url && (
              <a
                href={s.linkedin_url}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-linkedin"
                className="hover:text-white"
              >
                LinkedIn
              </a>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}
